import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { api } from '../../services/api';
import { AppDispatch } from '..';

// Função para carregar o usuário do localStorage
const loadUserFromLocalStorage = () => {
  try {
    const userData = localStorage.getItem('userData');
    if (userData) {
      return JSON.parse(userData);
    }
    return null;
  } catch (error) {
    console.error('Erro ao carregar usuário do localStorage:', error);
    return null;
  }
};

interface AuthState {
  user: any | null;
  token: string | null;
  isAuthenticated: boolean;
  error: string | null;
  resetPasswordSuccess: boolean;
}

const initialState: AuthState = {
  user: loadUserFromLocalStorage(),
  token: localStorage.getItem('acessToken'),
  isAuthenticated: !!localStorage.getItem('acessToken'),
  error: null,
  resetPasswordSuccess: false,
};

export const login = createAsyncThunk(
  'auth/login',
  async (
    {
      email,
      password,
      empresa,
    }: { email: string; password: string; empresa: string },
    { rejectWithValue }
  ) => {
    try {
      const data = await api.auth.login(email, password, empresa);
      if (!data) throw new Error('Falha na autenticação');
      data.user.empresa = empresa;
      localStorage.setItem('userData', JSON.stringify(data.user));
      localStorage.setItem('acessToken', data.token);
      return data;
    } catch (error: any) {
      return rejectWithValue(
        error.message || 'Erro ao fazer login. Verifique suas credenciais.'
      );
    }
  }
);

export const trocarSenha = createAsyncThunk(
  'auth/trocarSenha',
  async (
    { email, empresa }: { email: string; empresa: string },
    { rejectWithValue }
  ) => {
    try {
      const response = await api.auth.trocarSenha(empresa, email);
      return response;
    } catch (error: any) {
      return rejectWithValue(
        error.message || 'Erro ao solicitar redefinição de senha.'
      );
    }
  }
);

export const logout = createAsyncThunk('auth/logout', async () => {
  localStorage.removeItem('acessToken');
  localStorage.removeItem('userData');
  return null;
});

export const updateUserFromLocalStorage = createAsyncThunk(
  'auth/updateUserFromLocalStorage',
  async () => {
    return loadUserFromLocalStorage();
  }
);

export const setToken = createAsyncThunk(
  'auth/setToken',
  async (token: string) => {
    localStorage.setItem('acessToken', token);
    return token;
  }
);

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    clearResetPasswordSuccess: (state) => {
      state.resetPasswordSuccess = false;
    },
    loadUserData: (state) => {
      const userData = loadUserFromLocalStorage();
      if (userData) {
        state.user = userData;
      }
    },
  },
  extraReducers: (builder) => {
    builder
      // Login
      .addCase(login.pending, (state) => {
        state.error = null;
      })
      .addCase(login.fulfilled, (state, action: PayloadAction<any>) => {
        state.isAuthenticated = true;
        state.user = action.payload.user;
        state.token = action.payload.token;
      })
      .addCase(login.rejected, (state, action) => {
        state.error = action.payload as string;
      })
      // Trocar Senha
      .addCase(trocarSenha.pending, (state) => {
        state.error = null;
        state.resetPasswordSuccess = false;
      })
      .addCase(trocarSenha.fulfilled, (state) => {
        state.resetPasswordSuccess = true;
      })
      .addCase(trocarSenha.rejected, (state, action) => {
        state.error = action.payload as string;
      })
      // Logout
      .addCase(logout.fulfilled, (state) => {
        state.user = null;
        state.token = null;
        state.isAuthenticated = false;
      })
      .addCase(updateUserFromLocalStorage.fulfilled, (state, action) => {
        if (action.payload) {
          state.user = action.payload;
        }
      })
      // Token
      .addCase(setToken.fulfilled, (state, action) => {
        state.token = action.payload;
      });
  },
});

export const user = {
  menu: localStorage.getItem('estadoMenu') == 'aberto' || false,
  alternaMenu: () => {
    localStorage.setItem(
      'estadoMenu',
      localStorage.getItem('estadoMenu') == 'aberto' ? 'fechado' : 'aberto'
    );
  },
};

export const auth = {
  login: async (
    dispatch: AppDispatch,
    email: string,
    password: string,
    empresa: string
  ) => {
    await dispatch(login({ email, password, empresa }));
  },
  logout: async (dispatch: AppDispatch) => {
    await dispatch(logout());
  },
  setToken: (dispatch: AppDispatch, token: string) => {
    dispatch(setToken(token));
  },
};

export const { clearError, clearResetPasswordSuccess, loadUserData } =
  authSlice.actions;
export default authSlice.reducer;
